import chunk from 'lodash-es/chunk'
import dropWhile from 'lodash-es/dropWhile'
import flatten from 'lodash-es/flatten'
import round from 'lodash-es/round'

import {
  fileUploadedCallbackV3,
  getFileDocumentById,
  sendFileMetaDataV3,
  uploadFileV3,
} from '../../api'
import { EMPTY_LIST } from '../../constants/common'

import {
  FILE_SIZE_ROUND_PRECISION,
  FILE_UPLOAD_API_ERROR_TRANSLATION_KEY,
  FILE_UPLOAD_CHUNK_SIZE,
} from './File.constants'

function getNodeId(node) {
  return Array.isArray(node) ? Number(node[0]) : Number(node?.id)
}

function isNotCurrentDirectory(node, directoryId) {
  return getNodeId(node) !== directoryId
}

function createDirectoryCheck(dirId) {
  return function checkDirectory(node) {
    return isNotCurrentDirectory(node, dirId)
  }
}

function getRelatedFilePathList(path, directoryId) {
  if (!directoryId) {
    return path
  }

  const dirId = Number(directoryId)
  const checkDirectory = createDirectoryCheck(dirId)

  return dropWhile(path, checkDirectory)
}

function getBreadcrumbTitle(item, t) {
  if (item?.name) {
    return item.is_persistent ? t(item.name) : item.name
  }

  // v2 api format [id, name, is_persistent]
  return item[2] ? t(item[1]) : item[1]
}

async function uploadFile({ file, outboundUuid }) {
  const { data } = await sendFileMetaDataV3({
    data: {
      ...(file.external_id ? { external_id: file.external_id } : null),
      ...(file.height ? { height: file.height } : null),
      ...(file.meta ? { meta: file.meta } : null),
      ...(file.storage_type ? { storage_type: file.storage_type } : null),
      ...(file.width ? { width: file.width } : null),
      ...(file.set_permission_groups
        ? { set_permission_groups: file.set_permission_groups }
        : null),
      mime_type: file.mime_type,
      name: file.name,
      parent: file.parent,
      size: file.size,
    },
    outboundUuid,
  })

  if (data.id && data.signed_link) {
    await uploadFileV3({
      data: file.origin[0],
      endpoint: data.signed_link,
    })

    await fileUploadedCallbackV3({ id: data.id, outboundUuid })

    return { checking: true, id: data.id, name: file.name, size: file.size }
  }
}

async function getUploadedFileData({ file, outboundUuid }) {
  try {
    return await uploadFile({ file, outboundUuid })
  } catch (err) {
    return {
      ...file,
      error: {
        isPersistent: true,
        text:
          FILE_UPLOAD_API_ERROR_TRANSLATION_KEY[
            err?.response?.data?.errors?.origin
          ] || 'Error.UploadingFileApiErrorKey',
      },
      uploading: true,
    }
  }
}

async function getCheckedFile({ file, outboundUuid }) {
  let fileData = await getUploadedFileData({ file, outboundUuid })

  let count = 0
  let timeout = 5000

  while (fileData.checking || fileData.message) {
    count++

    fileData = await new Promise((resolve, reject) =>
      setTimeout(() => {
        getFileDocumentById({ id: fileData.id, outboundUuid })
          .then(data => resolve(data.data))
          .catch(err =>
            reject({
              ...file,
              error: {
                isPersistent: true,
                text:
                  FILE_UPLOAD_API_ERROR_TRANSLATION_KEY[
                    err?.response?.data?.errors?.origin
                  ] || 'Error.UploadingFileApiErrorKey',
              },
              uploading: true,
            })
          )
      }, timeout)
    )

    if (count >= 10) {
      timeout = 10000
    }

    if (count === 25) {
      break
    }
  }

  return fileData
}

// Caught per file so one failed poll does not drop the files that did upload
async function getCheckedFileList({ files, outboundUuid, uploadParams }) {
  return Promise.all(
    files.map(file =>
      getCheckedFile({
        file: { ...file, ...uploadParams },
        outboundUuid,
      }).catch(rejection => toFailedFileListItem(file, rejection))
    )
  )
}

async function getUploadedFileList({ files }) {
  return Promise.all(files.map(file => getUploadedFileData({ file })))
}

// `getCheckedFile` rejects with an error-shaped copy of the file
export function toFailedFileListItem(file, rejection) {
  return {
    ...file,
    error:
      rejection?.name === file.name && rejection.error
        ? rejection.error
        : { isPersistent: true, text: 'Error.UploadingFileApiErrorKey' },
    uploading: true,
  }
}

export async function getCheckedFilesListByChunk({
  chunkSize = FILE_UPLOAD_CHUNK_SIZE,
  files,
  getFilesList,
  outboundUuid,
  uploadParams,
}) {
  const chunks = chunk(files, chunkSize)

  const checkedFiles = []

  for (const chunk of chunks) {
    const checkedFilesList = await getFilesList({
      files: chunk,
      outboundUuid,
      uploadParams,
    })

    checkedFiles.push(checkedFilesList)
  }

  return flatten(checkedFiles)
}

export function getFileValueSize({
  minValue = 1,
  precision = FILE_SIZE_ROUND_PRECISION,
  unit = 'Kb',
  value,
}) {
  return `${Math.max(round(value * precision), minValue)}${unit}`
}

export function getFileSize({
  file,
  minValue = 1,
  precision = FILE_SIZE_ROUND_PRECISION,
  unit = 'Kb',
}) {
  return `(${getFileValueSize({ minValue, precision, unit, value: file.size })})`
}

export async function getFilesCheckedList({
  files,
  outboundUuid,
  uploadParams,
}) {
  return getCheckedFilesListByChunk({
    files,
    getFilesList: getCheckedFileList,
    outboundUuid,
    uploadParams,
  })
}

export async function getFilesUploadedList({ files }) {
  return getCheckedFilesListByChunk({
    files,
    getFilesList: getUploadedFileList,
  })
}

export function getSkeletonFiles(length = 3) {
  return Array.from({ length }).map((_, index) => ({ id: index, name: index }))
}

export function toFileWithCorrectIdKey(file, incorrectIdKey = 'file_id') {
  return {
    ...file,
    id: file[incorrectIdKey],
  }
}

export function getFilesBreadcrumbs({
  directoryId,
  firstBreadcrumbName,
  handleClickOption,
  path,
  sub,
  subMenu,
  t,
}) {
  if (!Array.isArray(path) || path.length === 0) {
    return EMPTY_LIST
  }

  let titleSub

  if (subMenu?.items.length > 0 && sub) {
    titleSub = subMenu.items.find(item => item.name === sub)?.title
  }

  const pathList = getRelatedFilePathList(path, directoryId)

  return [
    ...pathList.map((item, i) => {
      const id = getNodeId(item)

      return {
        id,
        title:
          i === 0 && firstBreadcrumbName
            ? firstBreadcrumbName
            : getBreadcrumbTitle(item, t),
        onClick: () => handleClickOption?.(id),
      }
    }),
    ...(titleSub ? [{ id: titleSub, title: t(titleSub) }] : EMPTY_LIST),
  ]
}
